/**
 * Tab ID Constants
 *
 * These constants represent the unique identifiers for each tab in the application.
 * Use these constants instead of string literals to ensure consistency and avoid typos.
 *
 * Usage:
 * import { TAB_IDS } from './TabConfig';
 *
 * // Instead of: "MY_CHECKLIST"
 * // Use: TAB_IDS.MY_CHECKLIST
 */
export const TAB_IDS = {
  MY_CHECKLIST: "MY_CHECKLIST",
  DETAILS: "DETAILS",
  CO_LENDING: "CO_LENDING",
  VERIFICATIONS: "VERIFICATIONS",
  ACTIVITY_HISTORY: "ACTIVITY_HISTORY",
  WORKFLOW_TIMELINE: "WORKFLOW_TIMELINE",
  CREDITLINE_DETAILS: "CREDITLINE_DETAILS",
  CREDITLINE_TRANSACTIONS: "CREDITLINE_TRANSACTIONS",
  ALL_DOCUMENTS: "ALL_DOCUMENTS",
  DISBURSEMENT: "DISBURSEMENT",
  APPLICATION_FORM: "APPLICATION_FORM",
  COLLATERAL_DETAILS: "COLLATERAL_DETAILS",
  UNDERWRITING: "UNDERWRITING",
  KYC: "KYC",
  SUMMARY: "SUMMARY",
  CO_APPLICANTS: "CO_APPLICANTS",
  DIRECTORS: "DIRECTORS",
  LOAN_OFFER: "LOAN_OFFER",
  INSURANCE: "INSURANCE",
  E_REGISTRATIONS: "E_REGISTRATIONS",
  MANUAL_CREDIT_REVIEW: "MANUAL_CREDIT_REVIEW",
  MOVE_TO_SALES: "MOVE_TO_SALES",
  BANK_INFO: "BANK_INFO",
  PARTNER_DETAILS: "PARTNER_DETAILS",
  PAYMENTS: "PAYMENTS",
  ADDITIONAL_DOCS: "ADDITIONAL_DOCS",
  NOTES: "NOTES",
  BUSINESS_DETAILS: "BUSINESS_DETAILS",
  DEVIATIONS: "DEVIATIONS",
  NEUROFIN: "NEUROFIN"
} as const;

// Type for tab IDs to ensure type safety
export type TabId = (typeof TAB_IDS)[keyof typeof TAB_IDS];

interface Tab {
  id: TabId;
  name: string;
  href: string;
  current: boolean;
  pageName?: string;
  isChildren?: boolean;
  order: number;
}

export const TABS_CONFIG: Record<string, Tab> = {
  "MY CHECKLIST": {
    id: TAB_IDS.MY_CHECKLIST,
    name: "My Checklist",
    href: "/task-management",
    current: false,
    order: 1
  },
  DETAILS: {
    id: TAB_IDS.DETAILS,
    name: "Details",
    href: "/",
    current: true,
    order: 2
  },
  COLENDING: {
    id: TAB_IDS.CO_LENDING,
    name: "Co-Lending",
    href: "/co-lending",
    current: false,
    isChildren: true,
    order: 3
  },
  VERIFICATIONS: {
    id: TAB_IDS.VERIFICATIONS,
    name: "Verifications",
    href: "/verification/my-verifications",
    current: false,
    isChildren: true,
    order: 9
  },
  "ACTIVITY HISTORY": {
    id: TAB_IDS.ACTIVITY_HISTORY,
    name: "Activity History",
    href: "/timeline",
    current: false,
    order: 15
  },
  "WORKFLOW TIMELINE": {
    id: TAB_IDS.WORKFLOW_TIMELINE,
    name: "Workflow Timeline",
    href: "/workflow-timeline",
    current: false,
    order: 20
  },
  "CREDITLINE DETAILS": {
    id: TAB_IDS.CREDITLINE_DETAILS,
    name: "CreditLine Details",
    href: "/creditline-details",
    current: false,
    order: 17
  },
  "CREDITLINE TRANSACTIONS": {
    id: TAB_IDS.CREDITLINE_TRANSACTIONS,
    name: "CreditLine Transactions",
    href: "/creditline-transactions",
    current: false,
    order: 16
  },
  "ALL DOCUMENTS": {
    id: TAB_IDS.ALL_DOCUMENTS,
    name: "All Documents",
    href: "/all-documents",
    current: false,
    order: 6
  },
  DISBURSEMENT: {
    id: TAB_IDS.DISBURSEMENT,
    name: "Disbursement",
    href: "/disbursement",
    current: false,
    order: 14
  },
  "APPLICATION FORM": {
    id: TAB_IDS.APPLICATION_FORM,
    name: "Application Form",
    href: "/application-form",
    current: false,
    order: 3
  },
  "COLLATERAL DETAILS": {
    id: TAB_IDS.COLLATERAL_DETAILS,
    name: "Collateral Details",
    href: "/collateral-details",
    current: false,
    order: 5
  },
  UNDERWRITING: {
    id: TAB_IDS.UNDERWRITING,
    name: "Underwriting",
    href: "/underwriting",
    current: false,
    isChildren: true,
    order: 8
  },
  KYC: {
    id: TAB_IDS.KYC,
    name: "KYC",
    href: "/kyc",
    current: false,
    order: 7
  },
  SUMMARY: {
    id: TAB_IDS.SUMMARY,
    name: "Summary",
    href: "/summary",
    current: false,
    order: 18
  },
  "CO APPLICANTS": {
    id: TAB_IDS.CO_APPLICANTS,
    name: "Co-applicants",
    href: "/co-applicants",
    current: false,
    order: 4
  },
  DIRECTORS: {
    id: TAB_IDS.DIRECTORS,
    name: "Directors",
    href: "/directors",
    current: false,
    order: 19
  },
  "LOAN OFFER": {
    id: TAB_IDS.LOAN_OFFER,
    name: "Loan Offer",
    href: "/loan-offer",
    current: false,
    order: 10
  },
  INSURANCE: {
    id: TAB_IDS.INSURANCE,
    name: "Insurance",
    href: "/insurance",
    current: false,
    order: 12
  },
  "E-REGISTRATIONS": {
    id: TAB_IDS.E_REGISTRATIONS,
    name: "E-Registrations",
    href: "/e-registrations/penny-drop",
    current: false,
    order: 13
  },
  "MANUAL CREDIT REVIEW": {
    id: TAB_IDS.MANUAL_CREDIT_REVIEW,
    name: "Manual Credit Review",
    href: "/credit-review",
    current: false,
    order: 11
  },
  "MOVE TO SALES": {
    id: TAB_IDS.MOVE_TO_SALES,
    name: "Move to Sales",
    href: "/pl-sanction-condition",
    current: false,
    order: 21
  },
  "BANK INFO": {
    id: TAB_IDS.BANK_INFO,
    name: "Bank Info",
    href: "/bank-info",
    current: false,
    order: 22
  },
  "PARTNER DETAILS": {
    id: TAB_IDS.PARTNER_DETAILS,
    name: "Partner Details",
    href: "/partner-details",
    current: false,
    order: 23
  },
  PAYMENTS: {
    id: TAB_IDS.PAYMENTS,
    name: "Payments",
    href: "/payments",
    current: false,
    order: 24
  },
  "ADDITIONAL DOCS": {
    id: TAB_IDS.ADDITIONAL_DOCS,
    name: "Additional Docs",
    href: "/additional-docs",
    current: false,
    order: 25
  },
  NOTES: {
    id: TAB_IDS.NOTES,
    name: "Notes",
    href: "/notes",
    current: false,
    order: 26
  },
  "BUSINESS DETAILS": {
    id: TAB_IDS.BUSINESS_DETAILS,
    name: "Business Details",
    href: "/business-details",
    current: false,
    order: 27
  }
};
